import { FC, useMemo } from "react";
import { Button } from "@/components/button";
import { ColumnDef } from "@tanstack/react-table";
import { SpaceAccountSummarySchema } from "@/domains/openapi/types";
import { Table } from "@/components/table/table";
import { UuidFieldRow } from "@/components/table/uuid-field-row";
import { TextFieldRow } from "@/components/table/text-field-row";
import { DateTimeFieldRow } from "@/components/table/datetime-field-row";
import { BooleanFieldRow } from "@/components/table/boolean-field-row";
import { DateTime } from "luxon";
import { orderBy } from "lodash-es";

interface SpaceAccountsTableProps {
  data: SpaceAccountSummarySchema[];
  onViewSpaceAccount: (id: string) => void;
  pageSize: number;
}

export const SpaceAccountsTable: FC<SpaceAccountsTableProps> = ({
  data,
  onViewSpaceAccount,
  pageSize,
}) => {
  const columns = useMemo<ColumnDef<SpaceAccountSummarySchema>[]>(
    () => [
      {
        header: "SpaceAccount ID",
        cell: ({ row }) => <UuidFieldRow id={row.original.id} />,
      },
      {
        header: "Account ID",
        cell: ({ row }) => <UuidFieldRow id={row.original.account_id} />,
      },
      {
        header: "Name",
        cell: ({ row }) => <TextFieldRow value={row.original.profile_display_name} />,
      },
      {
        header: "Email",
        cell: ({ row }) => <TextFieldRow value={row.original.profile_email_address} />,
      },
      {
        header: "Last Sync Query",
        cell: ({ row }) => <DateTimeFieldRow dateStr={row.original.last_queried_at} />,
      },
      {
        header: "Provisioned At",
        cell: ({ row }) => <DateTimeFieldRow dateStr={row.original.provisioned_at} />,
      },
      {
        header: "Google Account?",
        cell: ({ row }) => (
          <BooleanFieldRow value={row.original.has_google_account_authentication ?? null} />
        ),
      },
      {
        header: "Soft Deleted?",
        cell: ({ row }) => <BooleanFieldRow value={row.original.is_soft_deleted ?? null} />,
      },
      {
        header: "View",
        cell: ({ row }) => (
          <Button size="1" onClick={() => onViewSpaceAccount(row.original.id)}>
            View
          </Button>
        ),
      },
    ],
    [onViewSpaceAccount]
  );

  const sortedData = useMemo(() => {
    return orderBy(
      data,
      x => (x.last_queried_at ? DateTime.fromISO(x.last_queried_at).toMillis() : -Infinity),
      "desc"
    );
  }, [data]);

  return <Table data={sortedData} columns={columns} pageSize={pageSize} />;
};
